import {
  findFrenchDepartmentByCode,
  formatSocialSecurityNumber,
  formatUtcCalendarDay,
  fromUtcCalendarDay,
  normalizeSocialSecurityNumber,
  readableAddress,
} from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { useEffect, useState } from 'react';
import {
  AssistantProfileFormValues,
  useAssistantProfileForm,
} from '@/components/forms/assistant-profile-form';
import {
  DoctorProfileFormValues,
  useDoctorProfileForm,
} from '@/components/forms/doctor-profile-form';
import { EditAdminProfileFormValues } from '@/components/forms/edit-admin-profile/schema';
import { useEditAdminProfileForm } from '@/components/forms/edit-admin-profile/use-edit-admin-profile-form';
import useEditAdminProfileMutation from '@/hooks/mutations/use-edit-admin-profile';
import useEditAssistantProfileMutation from '@/hooks/mutations/use-edit-assistant-profile';
import useEditAttendingProfileMutation from '@/hooks/mutations/use-edit-attending-profile';
import useEditUserProfileMutation from '@/hooks/mutations/use-edit-user-profile';
import useLogoutMutation from '@/hooks/mutations/use-logout';
import {
  DoctorAttendingMe,
  GetMeResponse,
  useGetMeQuery,
} from '@/hooks/queries/use-me';
import { parseDateOnly } from '@/utils/dates/parse-date-only';
import { readableIban } from '@/utils/doctor';
import { clearObjectUrl } from '@/utils/file';
import { getInitials } from '@/utils/get-initials';

/**
 * Names the conseil départemental that delivered a licence, e.g. `Rhône (69)`.
 *
 * @param code - the stored département code, or `null`
 * @returns the label, or `null` when unknown
 */
const readableLicenceCouncil = (code: string | null): string | null => {
  const department = findFrenchDepartmentByCode(code);
  return department ? `${department.label} (${department.code})` : null;
};

/**
 * Formats a date as the `yyyy-MM-dd` value expected by `<input type="date">`.
 *
 * @param date - the date to format, or `null`
 * @returns the ISO date portion, or an empty string when `null`
 */
const toDateInputValue = (date: Date | null): string => {
  return date ? date.toISOString().slice(0, 10) : '';
};

const useEditDoctorProfileState = ({
  me,
  setMe,
}: {
  me: GetMeResponse;
  setMe: (_me: GetMeResponse) => void;
}) => {
  // Use unified form with 'substitute-profile' mode for self-edit
  const { doctorProfileForm } = useDoctorProfileForm({
    mode: 'substitute-profile',
  });

  const initForm = (me: GetMeResponse) => {
    const doctorSubstitute = me.doctorSubstitute;
    if (!doctorSubstitute) return;

    const oldPicture = doctorProfileForm.getValues().picture ?? undefined;
    clearObjectUrl(oldPicture);

    doctorProfileForm.reset({
      firstname: me.firstname,
      phoneNumber: me.phoneNumber ?? '',
      lastname: me.lastname,
      picture: me.picture ?? '',
      file: null,
      dateOfBirth: doctorSubstitute.dateOfBirth,
      address: doctorSubstitute.address,
      orderRegistrationNumber: doctorSubstitute.orderRegistrationNumber,
      rppsNumber: doctorSubstitute.rppsNumber,
      signatureUrl: doctorSubstitute.signatureUrl,
      iban: doctorSubstitute.iban,
      bic: doctorSubstitute.bic ?? '',
      thesis: doctorSubstitute.thesis ? 'thesis' : 'nothesis',
      // Structured rhythm fields
      onSiteSlotsWithoutAssistant:
        doctorSubstitute.onSiteSlotsWithoutAssistant ?? null,
      remoteSlotsWithoutAssistant:
        doctorSubstitute.remoteSlotsWithoutAssistant ?? null,
      onSiteSlotsWithAssistant:
        doctorSubstitute.onSiteSlotsWithAssistant ?? null,
      remoteSlotsWithAssistant:
        doctorSubstitute.remoteSlotsWithAssistant ?? null,
      // Contract profile (issue #796)
      urssafNumber: doctorSubstitute.urssafNumber ?? '',
      licenceNumber: doctorSubstitute.licenceNumber ?? '',
      licenceExpiresAt: doctorSubstitute.licenceExpiresAt
        ? fromUtcCalendarDay(doctorSubstitute.licenceExpiresAt)
        : null,
      licenceCouncilDepartmentCode:
        doctorSubstitute.licenceCouncilDepartmentCode ?? null,
    });
  };

  useEffect(() => {
    initForm(me);
  }, [me]);

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const { mutate: editUserProfile, isPending } = useEditUserProfileMutation();

  const enabledEditDoctor = () => {
    initForm(me);
    setShowEdit(true);
  };

  const closeEdit = (_: { success: boolean }) => {
    setShowEdit(false);
  };

  const cancelEdit = () => {
    setShowEdit(false);
  };

  const submitForm = (values: DoctorProfileFormValues) => {
    editUserProfile(
      {
        firstname: values.firstname,
        phoneNumber: values.phoneNumber,
        lastname: values.lastname,
        address: values.address!,
        dateOfBirth: values.dateOfBirth!,
        orderRegistrationNumber: values.orderRegistrationNumber,
        rppsNumber: values.rppsNumber!,
        iban: values.iban,
        bic: values.bic,
        file: values.file,
        signatureFile: values.signatureFile ?? undefined,
        thesis: values.thesis === 'thesis',
        // Structured rhythm fields
        onSiteSlotsWithoutAssistant: values.onSiteSlotsWithoutAssistant ?? null,
        remoteSlotsWithoutAssistant: values.remoteSlotsWithoutAssistant ?? null,
        onSiteSlotsWithAssistant: values.onSiteSlotsWithAssistant ?? null,
        remoteSlotsWithAssistant: values.remoteSlotsWithAssistant ?? null,
        deleteAvatar: !values.picture,
        urssafNumber: values.urssafNumber ?? null,
        licenceNumber: values.licenceNumber ?? null,
        licenceExpiresAt: values.licenceExpiresAt ?? null,
        licenceCouncilDepartmentCode:
          values.licenceCouncilDepartmentCode ?? null,
      },
      {
        onSuccess: (updatedMe) => {
          closeEdit({ success: true });

          setMe({
            ...me,
            firstname: updatedMe.firstname,
            phoneNumber: updatedMe.phoneNumber,
            lastname: updatedMe.lastname,
            picture: updatedMe.picture ?? null,
            doctorSubstitute: {
              ...me.doctorSubstitute!,
              dateOfBirth: updatedMe.dateOfBirth,
              address: {
                ...updatedMe.address,
                state: updatedMe.address.state,
              },
              orderRegistrationNumber: updatedMe.orderRegistrationNumber,
              signatureUrl: updatedMe.signatureUrl,
              iban: updatedMe.iban,
              bic: updatedMe.bic,
              thesis: updatedMe.thesis,
              onSiteSlotsWithoutAssistant:
                updatedMe.onSiteSlotsWithoutAssistant,
              remoteSlotsWithoutAssistant:
                updatedMe.remoteSlotsWithoutAssistant,
              onSiteSlotsWithAssistant: updatedMe.onSiteSlotsWithAssistant,
              remoteSlotsWithAssistant: updatedMe.remoteSlotsWithAssistant,
              urssafNumber: updatedMe.urssafNumber,
              licenceNumber: updatedMe.licenceNumber,
              licenceExpiresAt: updatedMe.licenceExpiresAt,
              licenceCouncilDepartmentCode:
                updatedMe.licenceCouncilDepartmentCode,
            },
          });
        },
      },
    );
  };

  return {
    api: {
      open: enabledEditDoctor,
      submit: (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();
        doctorProfileForm.trigger();
        doctorProfileForm.handleSubmit(submitForm)();
      },
      close: closeEdit,
      cancel: cancelEdit,
    },
    form: doctorProfileForm,
    mode: 'substitute-profile' as const,
    data: {
      show: showEdit,
      saving: isPending,
    },
  };
};

/**
 * Helper to extract doctor profile from the first doctorAttending item
 * (profile fields are only on the first item since Doctor is shared across offices)
 */
const getDoctorAttendingProfile = (
  me: GetMeResponse,
): DoctorAttendingMe | null => {
  const firstAttending = me.doctorAttending?.[0];
  if (!firstAttending || !firstAttending.orderRegistrationNumber) {
    return null;
  }
  return firstAttending;
};

const useEditAttendingProfileState = ({
  me,
  setMe,
}: {
  me: GetMeResponse;
  setMe: (_me: GetMeResponse) => void;
}) => {
  // Use the unified form with 'attending-profile' mode for self-edit
  const { doctorProfileForm } = useDoctorProfileForm({
    mode: 'attending-profile',
  });

  const initForm = (me: GetMeResponse) => {
    const oldPicture = doctorProfileForm.getValues().picture ?? undefined;
    clearObjectUrl(oldPicture);

    const doctorAttending = getDoctorAttendingProfile(me);
    if (!doctorAttending) return;

    doctorProfileForm.reset({
      firstname: me.firstname,
      phoneNumber: me.phoneNumber ?? '',
      lastname: me.lastname,
      picture: me.picture ?? '',
      file: null,
      dateOfBirth: doctorAttending.dateOfBirth ?? undefined,
      address: doctorAttending.address ?? {
        line1: '',
        line2: '',
        city: '',
        postalCode: '',
      },
      orderRegistrationNumber: doctorAttending.orderRegistrationNumber ?? '',
      rppsNumber: doctorAttending.rppsNumber ?? '',
      signatureUrl: doctorAttending.signatureUrl,
      iban: doctorAttending.iban ?? '',
      bic: doctorAttending.bic ?? '',
      thesis: doctorAttending.thesis ? 'thesis' : 'nothesis',
      // Structured rhythm fields
      onSiteSlotsWithoutAssistant:
        doctorAttending.onSiteSlotsWithoutAssistant ?? null,
      remoteSlotsWithoutAssistant:
        doctorAttending.remoteSlotsWithoutAssistant ?? null,
      onSiteSlotsWithAssistant:
        doctorAttending.onSiteSlotsWithAssistant ?? null,
      remoteSlotsWithAssistant:
        doctorAttending.remoteSlotsWithAssistant ?? null,
    });
  };

  useEffect(() => {
    initForm(me);
  }, [me]);

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const { mutate: editAttendingProfile, isPending } =
    useEditAttendingProfileMutation();

  const enabledEditDoctor = () => {
    initForm(me);
    setShowEdit(true);
  };

  const closeEdit = (_: { success: boolean }) => {
    setShowEdit(false);
  };

  const cancelEdit = () => {
    setShowEdit(false);
  };

  const submitForm = (values: DoctorProfileFormValues) => {
    editAttendingProfile(
      {
        firstname: values.firstname,
        phoneNumber: values.phoneNumber,
        lastname: values.lastname,
        address: values.address!,
        dateOfBirth: values.dateOfBirth!,
        orderRegistrationNumber: values.orderRegistrationNumber,
        rppsNumber: values.rppsNumber!,
        iban: values.iban,
        bic: values.bic,
        file: values.file,
        signatureFile: values.signatureFile ?? undefined,
        thesis: values.thesis === 'thesis',
        // Structured rhythm fields
        onSiteSlotsWithoutAssistant: values.onSiteSlotsWithoutAssistant ?? null,
        remoteSlotsWithoutAssistant: values.remoteSlotsWithoutAssistant ?? null,
        onSiteSlotsWithAssistant: values.onSiteSlotsWithAssistant ?? null,
        remoteSlotsWithAssistant: values.remoteSlotsWithAssistant ?? null,
        deleteAvatar: !values.picture,
      },
      {
        onSuccess: (updatedMe) => {
          closeEdit({ success: true });

          // Update the first doctorAttending item with the new profile data
          const updatedDoctorAttending = me.doctorAttending?.map(
            (da, index) => {
              if (index === 0) {
                return {
                  ...da,
                  dateOfBirth: updatedMe.dateOfBirth,
                  address: updatedMe.address,
                  orderRegistrationNumber: updatedMe.orderRegistrationNumber,
                  rppsNumber: updatedMe.rppsNumber ?? undefined,
                  signatureUrl: updatedMe.signatureUrl,
                  iban: updatedMe.iban,
                  bic: updatedMe.bic,
                  thesis: updatedMe.thesis,
                  onSiteSlotsWithoutAssistant:
                    updatedMe.onSiteSlotsWithoutAssistant,
                  remoteSlotsWithoutAssistant:
                    updatedMe.remoteSlotsWithoutAssistant,
                  onSiteSlotsWithAssistant: updatedMe.onSiteSlotsWithAssistant,
                  remoteSlotsWithAssistant: updatedMe.remoteSlotsWithAssistant,
                };
              }
              return da;
            },
          );

          setMe({
            ...me,
            firstname: updatedMe.firstname,
            phoneNumber: updatedMe.phoneNumber,
            lastname: updatedMe.lastname,
            picture: updatedMe.picture ?? null,
            doctorAttending: updatedDoctorAttending,
          });
        },
      },
    );
  };

  return {
    api: {
      open: enabledEditDoctor,
      submit: (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();
        doctorProfileForm.trigger();
        doctorProfileForm.handleSubmit(submitForm)();
      },
      close: closeEdit,
      cancel: cancelEdit,
    },
    form: doctorProfileForm,
    mode: 'attending-profile' as const,
    data: {
      show: showEdit,
      saving: isPending,
    },
  };
};

const useEditAssistantProfileState = ({
  me,
  setMe,
}: {
  me: GetMeResponse;
  setMe: (_me: GetMeResponse) => void;
}) => {
  const { assistantProfileForm } = useAssistantProfileForm();

  const initForm = (me: GetMeResponse) => {
    const profile = me.medicalAssistantProfile;
    if (!profile) return;

    assistantProfileForm.reset({
      firstname: me.firstname,
      lastname: me.lastname,
      phone: profile.phone ?? '',
      dateOfBirth: toDateInputValue(profile.dateOfBirth),
      birthPlace: profile.birthPlace ?? '',
      nationality: profile.nationality ?? '',
      socialSecurityNumber: formatSocialSecurityNumber(
        profile.socialSecurityNumber ?? '',
      ),
      addressLine1: profile.address?.line1 ?? '',
      addressLine2: profile.address?.line2 ?? '',
      addressPostalCode: profile.address?.postalCode ?? '',
      addressCity: profile.address?.city ?? '',
      addressState: profile.address?.state ?? '',
    });
  };

  useEffect(() => {
    initForm(me);
  }, [me]);

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const { mutate: editAssistantProfile, isPending } =
    useEditAssistantProfileMutation();

  const enabledEdit = () => {
    initForm(me);
    setShowEdit(true);
  };

  const closeEdit = (_: { success: boolean }) => {
    setShowEdit(false);
  };

  const cancelEdit = () => {
    setShowEdit(false);
  };

  const submitForm = (values: AssistantProfileFormValues) => {
    const address = {
      line1: values.addressLine1,
      line2: values.addressLine2 || undefined,
      postalCode: values.addressPostalCode,
      city: values.addressCity,
      state: values.addressState || undefined,
    };

    editAssistantProfile(
      {
        firstname: values.firstname,
        lastname: values.lastname,
        phone: values.phone,
        dateOfBirth: values.dateOfBirth,
        birthPlace: values.birthPlace,
        nationality: values.nationality,
        socialSecurityNumber: normalizeSocialSecurityNumber(
          values.socialSecurityNumber,
        ),
        address,
      },
      {
        onSuccess: () => {
          closeEdit({ success: true });

          setMe({
            ...me,
            firstname: values.firstname,
            lastname: values.lastname,
            medicalAssistantProfile: me.medicalAssistantProfile
              ? {
                  ...me.medicalAssistantProfile,
                  phone: values.phone,
                  dateOfBirth: parseDateOnly(values.dateOfBirth),
                  birthPlace: values.birthPlace,
                  nationality: values.nationality,
                  socialSecurityNumber: normalizeSocialSecurityNumber(
                    values.socialSecurityNumber,
                  ),
                  address,
                }
              : undefined,
          });
        },
      },
    );
  };

  return {
    api: {
      open: enabledEdit,
      submit: (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault();
        assistantProfileForm.trigger();
        assistantProfileForm.handleSubmit(submitForm)();
      },
      close: closeEdit,
      cancel: cancelEdit,
    },
    form: assistantProfileForm,
    data: {
      show: showEdit,
      saving: isPending,
    },
  };
};

const useEditBasicProfileState = ({
  me,
  setMe,
}: {
  me: GetMeResponse;
  setMe: (_me: GetMeResponse) => void;
}) => {
  const { editAdminProfileForm } = useEditAdminProfileForm();

  const initForm = (me: GetMeResponse) => {
    editAdminProfileForm.reset({
      firstname: me.firstname,
      lastname: me.lastname,
      picture: me.picture ?? '',
      file: null,
    });
  };

  useEffect(() => {
    initForm(me);
  }, [me]);

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const { mutate: editAdminProfile, isPending } = useEditAdminProfileMutation();

  const enabledEditDoctor = () => {
    initForm(me);
    setShowEdit(true);
  };

  const closeEdit = (_: { success: boolean }) => {
    setShowEdit(false);
  };

  const cancelEdit = () => {
    setShowEdit(false);
  };

  const submitForm = (values: EditAdminProfileFormValues) => {
    editAdminProfile(
      {
        firstname: values.firstname,
        lastname: values.lastname,
        file: values.file,
        deleteAvatar: !values.picture,
      },
      {
        onSuccess: (updatedMe) => {
          closeEdit({ success: true });

          setMe({
            ...me,
            firstname: updatedMe.firstname,
            lastname: updatedMe.lastname,
            picture: updatedMe.picture ?? null,
          });
        },
      },
    );
  };

  return {
    api: {
      open: enabledEditDoctor,
      submit: () => {
        editAdminProfileForm.trigger();
        editAdminProfileForm.handleSubmit(submitForm)();
      },
      close: closeEdit,
      cancel: cancelEdit,
    },
    form: editAdminProfileForm,
    data: {
      show: showEdit,
      saving: isPending,
    },
  };
};

type EditProfileState =
  | ReturnType<typeof useEditDoctorProfileState>
  | ReturnType<typeof useEditAttendingProfileState>
  | ReturnType<typeof useEditAssistantProfileState>
  | ReturnType<typeof useEditBasicProfileState>;

// Type guard to check if the profile has unified form mode
export const hasUnifiedFormMode = (
  state: EditProfileState,
): state is
  | ReturnType<typeof useEditDoctorProfileState>
  | ReturnType<typeof useEditAttendingProfileState> => {
  return 'mode' in state;
};

export const selectViewProfileViewModel = () => {
  const { data: meResponse } = useGetMeQuery();
  const { mutate: logout } = useLogoutMutation();

  const [me, setMe] = useState<GetMeResponse>(meResponse!);
  const { doctorSubstitute } = me;
  const doctorAttending = getDoctorAttendingProfile(me);
  const medicalAssistant = me.medicalAssistantProfile ?? null;

  // All edit-profile states must be created unconditionally so their hooks
  // run in a stable order on every render (Rules of Hooks). The relevant one
  // is selected afterwards based on the user's profile type.
  const doctorEditProfile = useEditDoctorProfileState({ me, setMe });
  const attendingEditProfile = useEditAttendingProfileState({ me, setMe });
  const assistantEditProfile = useEditAssistantProfileState({ me, setMe });
  const basicEditProfile = useEditBasicProfileState({ me, setMe });

  let editProfile: EditProfileState;
  if (me.doctorSubstitute) {
    editProfile = doctorEditProfile;
  } else if (doctorAttending) {
    editProfile = attendingEditProfile;
  } else if (medicalAssistant) {
    editProfile = assistantEditProfile;
  } else {
    editProfile = basicEditProfile;
  }

  // Determine if user has a full doctor profile (either substitute or attending)
  const hasDoctorProfile = !!me.doctorSubstitute || !!doctorAttending;
  const isMedicalAssistant = !!medicalAssistant;

  return {
    hasDoctorProfile,
    isMedicalAssistant,
    medicalAssistant: medicalAssistant
      ? {
          phone: medicalAssistant.phone,
          dateOfBirth:
            medicalAssistant.dateOfBirth &&
            intlFormat(medicalAssistant.dateOfBirth),
          birthPlace: medicalAssistant.birthPlace,
          nationality: medicalAssistant.nationality,
          socialSecurityNumber:
            medicalAssistant.socialSecurityNumber &&
            formatSocialSecurityNumber(medicalAssistant.socialSecurityNumber),
          address: medicalAssistant.address
            ? readableAddress(medicalAssistant.address)
            : null,
        }
      : null,
    information: {
      initials: getInitials(me),
      firstname: me.firstname,
      lastname: me.lastname,
      email: me.email,
      phoneNumber: me.phoneNumber,
      picture: me.picture ?? undefined,
    },
    doctorSubstitute: doctorSubstitute
      ? {
          dateOfBirth:
            doctorSubstitute.dateOfBirth &&
            intlFormat(doctorSubstitute.dateOfBirth),
          address: readableAddress(doctorSubstitute.address),
          orderRegistrationNumber: doctorSubstitute.orderRegistrationNumber,
          rppsNumber: doctorSubstitute.rppsNumber,
          iban: readableIban(doctorSubstitute.iban),
          bic: doctorSubstitute.bic,
          thesis: doctorSubstitute.thesis
            ? 'doctor.thesis.yes'
            : 'doctor.thesis.no',
          // Structured rhythm fields
          onSiteSlotsWithoutAssistant:
            doctorSubstitute.onSiteSlotsWithoutAssistant,
          remoteSlotsWithoutAssistant:
            doctorSubstitute.remoteSlotsWithoutAssistant,
          onSiteSlotsWithAssistant: doctorSubstitute.onSiteSlotsWithAssistant,
          remoteSlotsWithAssistant: doctorSubstitute.remoteSlotsWithAssistant,
          signatureUrl: doctorSubstitute.signatureUrl ?? undefined,
          urssafNumber: doctorSubstitute.urssafNumber,
          licence: doctorSubstitute.thesis
            ? null
            : {
                number: doctorSubstitute.licenceNumber,
                expiresAt: doctorSubstitute.licenceExpiresAt
                  ? formatUtcCalendarDay(doctorSubstitute.licenceExpiresAt)
                  : null,
                council: readableLicenceCouncil(
                  doctorSubstitute.licenceCouncilDepartmentCode,
                ),
              },
          documents: {
            id: doctorSubstitute.id,
            thesis: doctorSubstitute.thesis,
            licenceDocument: doctorSubstitute.licenceDocument,
            rcpDocument: doctorSubstitute.rcpDocument,
          },
        }
      : null,
    doctorAttending: doctorAttending
      ? {
          dateOfBirth:
            doctorAttending.dateOfBirth &&
            intlFormat(doctorAttending.dateOfBirth),
          address: doctorAttending.address
            ? readableAddress(doctorAttending.address)
            : null,
          orderRegistrationNumber: doctorAttending.orderRegistrationNumber,
          rppsNumber: doctorAttending.rppsNumber,
          iban: doctorAttending.iban
            ? readableIban(doctorAttending.iban)
            : null,
          bic: doctorAttending.bic,
          thesis: doctorAttending.thesis
            ? 'doctor.thesis.yes'
            : 'doctor.thesis.no',
          // Structured rhythm fields
          onSiteSlotsWithoutAssistant:
            doctorAttending.onSiteSlotsWithoutAssistant,
          remoteSlotsWithoutAssistant:
            doctorAttending.remoteSlotsWithoutAssistant,
          onSiteSlotsWithAssistant: doctorAttending.onSiteSlotsWithAssistant,
          remoteSlotsWithAssistant: doctorAttending.remoteSlotsWithAssistant,
          signatureUrl: doctorAttending.signatureUrl ?? undefined,
        }
      : null,
    editProfile,
    api: {
      logout: () => {
        logout();
      },
    },
  };
};
